import { ComponentType, FC, ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, useMediaQuery } from '@mui/material';

import { State, UserKindEnum } from '@/types';
import { useUserProfile } from '@/hooks/useUserProfile';
import {
  Footer,
  Header,
  Loader,
  ManagerWithoutEmployer,
  ForeignBrokerWithoutVerification,
  SidebarMenu,
} from '@/components';
import { ScrollTopButton } from '@/components/Dashboard/ScrollTopButton';
import { useNavigate } from 'react-router-dom';

import { DialogsProvider } from './DialogsContext';

const DASHBOARD_HEADER_HEIGHT = '72px';
const DASHBOARD_STICKY_GUTTER = '24px';
const DASHBOARD_BODY_LEFT_GUTTER = '16px';
const DASHBOARD_SIDEBAR_WIDTH = '250px';
const DASHBOARD_BODY_COLUMN_GAP = '15px';
const DASHBOARD_MAIN_GUTTER = '24px';
const DASHBOARD_PAGE_CONTENT_MAX_WIDTH = '1600px';
const DASHBOARD_WORKSPACE_MAX_WIDTH = `calc(${DASHBOARD_BODY_LEFT_GUTTER} + ${DASHBOARD_SIDEBAR_WIDTH} + ${DASHBOARD_BODY_COLUMN_GAP} + ${DASHBOARD_MAIN_GUTTER} + ${DASHBOARD_PAGE_CONTENT_MAX_WIDTH} + ${DASHBOARD_MAIN_GUTTER})`;

const DashboardFn = ({
  openSignupPopup,
  openLogIn,
  children,
  isChecked,
  isSearch,
}: {
  openSignupPopup?: boolean;
  openLogIn?: boolean;
  children: ReactNode;
  isChecked?: boolean;
  isSearch?: boolean;
  isSignUp?: boolean;
}) => {
  const [openHeaderBackdrop, setOpenHeaderBackdrop] = useState<boolean>();
  const [openSidebarBackdrop, setOpenSidebarBackdrop] = useState<boolean>();
  const [count, setCount] = useState<number>();
  const isAbove900 = useMediaQuery('(min-width:1200px)');
  const user = useSelector(({ auth }: State) => auth.user);
  const navigate = useNavigate();

  const { isLoading, isLoggedIn } = useUserProfile(() => {
    if (openSignupPopup) {
      navigate('/signup');
    }
    if (openLogIn) {
      navigate('/login');
    }
  });

  useEffect(() => {
    const stepsOfStudy = localStorage.getItem('study');
    if (isLoggedIn && stepsOfStudy !== null) {
      setCount(Number(stepsOfStudy));
      if (Number(stepsOfStudy) < 3) {
        setOpenHeaderBackdrop(true);
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (count === 4) {
      setOpenHeaderBackdrop(false);
      setOpenSidebarBackdrop(true);
    }
    if (count === 13) {
      setOpenSidebarBackdrop(false);
      localStorage.removeItem('study');
    }
  }, [count]);

  const isManager = user.kind === UserKindEnum.Manager;
  const isForeign = user.kind === UserKindEnum.Foreign;
  const isManagerPermission = isManager && user.employer !== null;
  const isForeignPermission = (isForeign && user.verified) || isChecked;
  const permission = () => !isManager || isManagerPermission || isChecked || isForeignPermission;

  const isHeaderVisible = count ? (count < 4 || count > 12 ? true : false) : true;
  const showDashboardHeader = (isLoggedIn || isAbove900) && isHeaderVisible;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      data-qa='dashboard-shell'
      sx={{
        '--dashboard-header-height': showDashboardHeader ? DASHBOARD_HEADER_HEIGHT : '0px',
        '--dashboard-sticky-gutter': DASHBOARD_STICKY_GUTTER,
        display: 'grid',
        gridTemplateAreas: "'header' 'body' 'footer'",
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
        minHeight: '100dvh',
        minWidth: 0,
        width: '100%',
      }}
    >
      {showDashboardHeader && (
        <Box
          data-qa='dashboard-header'
          sx={{
            gridArea: 'header',
            minWidth: 0,
            position: 'sticky',
            top: 0,
            width: '100%',
            zIndex: 1300,
          }}
        >
          <Header
            count={count}
            contentMaxWidth={DASHBOARD_WORKSPACE_MAX_WIDTH}
            setCount={setCount}
            openHeaderBackdrop={openHeaderBackdrop}
            setOpenHeaderBackdrop={setOpenHeaderBackdrop}
          />
        </Box>
      )}

      <Box
        data-qa='dashboard-body'
        sx={{
          alignItems: 'flex-start',
          boxSizing: 'border-box',
          display: 'grid',
          gap: {
            xs: 0,
            lg: isLoggedIn ? DASHBOARD_BODY_COLUMN_GAP : 0,
          },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: isLoggedIn ? `${DASHBOARD_SIDEBAR_WIDTH} minmax(0, 1fr)` : 'minmax(0, 1fr)',
          },
          gridArea: 'body',
          marginInline: 'auto',
          maxWidth: DASHBOARD_WORKSPACE_MAX_WIDTH,
          minWidth: 0,
          padding: {
            xs: '24px 0 56px',
            md: isLoggedIn ? '24px 0 56px' : 0,
            lg: isLoggedIn ? `24px 0 56px ${DASHBOARD_BODY_LEFT_GUTTER}` : 0,
          },
          width: '100%',
        }}
      >
        {!isLoading && (
          <>
            {isAbove900 && isLoggedIn && (
              <Box
                component='aside'
                data-qa='dashboard-sidebar'
                sx={{ alignSelf: 'stretch', minWidth: 0 }}
              >
                <SidebarMenu
                  count={count}
                  setCount={setCount}
                  openSidebarBackdrop={openSidebarBackdrop}
                  setOpenSidebarBackdrop={setOpenSidebarBackdrop}
                />
              </Box>
            )}
            <Box
              component='main'
              data-qa='dashboard-content'
              sx={{
                boxSizing: 'border-box',
                minWidth: 0,
                px: {
                  xs: '16px',
                  md: '20px',
                  lg: DASHBOARD_MAIN_GUTTER,
                },
                width: '100%',
              }}
            >
              <Box
                data-qa='dashboard-page-content'
                sx={{
                  marginInline: 'auto',
                  maxWidth: DASHBOARD_PAGE_CONTENT_MAX_WIDTH,
                  minWidth: 0,
                  width: '100%',
                }}
              >
                {isForeign && !isForeignPermission ? (
                  <ForeignBrokerWithoutVerification />
                ) : permission() ? (
                  children
                ) : (
                  <ManagerWithoutEmployer />
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
      <ScrollTopButton isSearch={isSearch} />
      <Box data-qa='dashboard-footer' sx={{ gridArea: 'footer', minWidth: 0, width: '100%' }}>
        <Footer contentMaxWidth={DASHBOARD_WORKSPACE_MAX_WIDTH} />
      </Box>
    </Box>
  );
};

export const withDialogProvider = <P extends object>(Component: ComponentType<P>): FC<P> => {
  const ComponentWithDialogProvider: FC<P> = (props) => (
    <DialogsProvider>
      <Component {...props} />
    </DialogsProvider>
  );

  ComponentWithDialogProvider.displayName = `withDialogProvider(${Component.displayName ?? 'Component'})`;
  return ComponentWithDialogProvider;
};

export const Dashboard = withDialogProvider(DashboardFn);
